import { API_BASE_URL } from '../config';
import api from './axiosInstance';

/**
 * Obtiene líneas de negocio paginadas del backend
 * @param {number} page - Número de página (comienza en 1)
 * @param {number} pageSize - Cantidad de registros por página
 * @param {boolean|null} isEnabled - Si true, solo activas; si false, solo inactivas; si null, todas
 * @param {string} search - Término de búsqueda (opcional)
 * @returns {Promise} - { page, pageSize, total, businessLines }
 */
export async function getBusinessLinesPaged(
  page = 1,
  pageSize = 10,
  status = 'Activos',
  search = null,
  sortBy = null,
  desc = null
) {
  try {
    const params = {
      page,
      pageSize,
    };

    // status: 'Todos' | 'Activos' | 'Inactivos' or boolean for isEnabled
    if (typeof status === 'boolean') {
      params.isEnabled = status;
    } else if (status && status !== 'Todos') {
      params.status = status;
    }

    // Solo agregar search si tiene valor
    if (search && search.trim()) {
      params.search = search.trim();
    }

    if (sortBy) {
      params.sortBy = sortBy;
    }

    if (desc !== null && desc !== undefined) {
      params.desc = desc;
    }

    const response = await api.get(`${API_BASE_URL}/BusinessLines/paged`, {
      headers: {
        'Content-Type': 'application/json',
      },
      params,
    });

    return {
      page: response.data.page,
      pageSize: response.data.pageSize,
      total: response.data.total,
      businessLines: response.data.data || [],
    };
  } catch (error) {
    console.error('getBusinessLinesPaged failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener el listado de líneas de negocio.';
    throw error;
  }
}

/**
 * Obtiene una línea de negocio por ID
 * @param {number} businessLineId - ID de la línea de negocio
 * @returns {Promise} - La línea de negocio
 */
export async function getBusinessLineById(businessLineId) {
  try {
    const response = await api.get(
      `${API_BASE_URL}/BusinessLines/${businessLineId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('getBusinessLineById failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo obtener la línea de negocio.';
    throw error;
  }
}

/**
 * Crea una nueva línea de negocio
 * @param {object} businessLineData - Datos de la línea de negocio { name, isEnabled }
 * @returns {Promise} - La línea de negocio creada
 */
export async function createBusinessLine(businessLineData) {
  try {
    const payload = {
      name: businessLineData.name || '',
    };

    const response = await api.post(
      `${API_BASE_URL}/BusinessLines/create`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('createBusinessLine failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo crear la línea de negocio.';
    throw error;
  }
}

/**
 * Actualiza una línea de negocio existente
 * @param {number} businessLineId - ID de la línea de negocio
 * @param {object} businessLineData - Datos actualizados { name, isEnabled }
 * @returns {Promise} - La línea de negocio actualizada
 */
export async function updateBusinessLine(businessLineId, businessLineData) {
  try {
    const payload = {
      name: businessLineData.name || '',
      isEnabled: businessLineData.isEnabled ?? true,
    };

    const response = await api.put(
      `${API_BASE_URL}/BusinessLines/${businessLineId}`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('updateBusinessLine failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo actualizar la línea de negocio.';
    throw error;
  }
}

/**
 * Elimina una línea de negocio (borrado lógico)
 * @param {number} businessLineId - ID de la línea de negocio a eliminar
 * @returns {Promise}
 */
export async function deleteBusinessLine(businessLineId) {
  try {
    const response = await api.delete(
      `${API_BASE_URL}/BusinessLines/${businessLineId}`,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    console.error('deleteBusinessLine failed:', error);
    console.error('Error details:', error.response?.data);
    error.customMessage = 'No se pudo eliminar la línea de negocio.';
    throw error;
  }
}
